import { useState } from 'react';
import { Box, Button, Icon, Popover, Tag } from '@nimbus-ds/components';
import { EllipsisIcon } from '@nimbus-ds/icons';
import { Menu, MenuButton } from '@nimbus-ds/patterns';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ActionProp, ApplicationsDesktop } from 'commons/components';
import useNuvemEnvioPrintLabel from 'domains/NuvemEnvioCommons/Orders/hooks/useNuvemEnvioPrintLabel/useNuvemEnvioPrintLabel';
import {
  isNuvemEnvioPrintLabelAvailable,
  transformBulkOrdersToOrdersForPrintLabel,
} from 'domains/NuvemEnvioCommons/Orders/utils';
import { getBulkOrders } from 'domains/Orders/Orders/ordersSlice';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';

interface ExtraOptionsHeaderDesktopProps {
  actions: ActionProp[];
  applications: ActionProp[];
  ordersIds: string[];
}

function ExtraOptionsHeaderDesktop({
  actions,
  applications,
  ordersIds,
}: Readonly<ExtraOptionsHeaderDesktopProps>) {
  const { t: tComm } = useTranslation('common');
  const t = useTranslationOrders();

  const [isVisiblePopoverOptions, setIsVisiblePopoverOptions] = useState(false);

  const bulkOrders = useSelector(getBulkOrders);

  const { printNuvemEnvioLabelsFromOrdersList } = useNuvemEnvioPrintLabel();

  const handleOnOptionClick = (action: ActionProp) => {
    const actionId = action.name ?? action.children ?? '';

    if (isNuvemEnvioPrintLabelAvailable(actionId)) {
      setIsVisiblePopoverOptions(false);
      if (ordersIds.length === 0) return;

      const ordersForPrint = transformBulkOrdersToOrdersForPrintLabel(
        ordersIds,
        bulkOrders,
      );
      printNuvemEnvioLabelsFromOrdersList(ordersForPrint);
      return;
    }
    action.onClick?.();
    setIsVisiblePopoverOptions(false);
  };

  if (applications.length === 0) return null;

  if (actions.length === 0)
    return <ApplicationsDesktop applications={applications} />;

  return (
    <Popover
      key="ordersListMenu"
      position="bottom"
      onVisibility={(visible) => {
        setIsVisiblePopoverOptions(visible);
      }}
      padding="small"
      visible={isVisiblePopoverOptions}
      content={
        <Box flexDirection="column">
          <Box padding="1" flexDirection="column">
            {actions.map((action) => (
              <MenuButton
                key={`action-${action.children}`}
                label={action.children ?? 'undefined'}
                startIcon={action.icon}
                onClick={() => handleOnOptionClick(action)}
              >
                {action.name === 'autocancel' && (
                  <Tag appearance="primary">{t('autoCancel.new')}</Tag>
                )}
              </MenuButton>
            ))}
          </Box>
          {applications && applications.length > 0 && (
            <Menu.Section title={tComm('myApps')}>
              <Box flexDirection="column" padding="1">
                {applications.map((application) => (
                  <MenuButton
                    key={`application-${application.children}`}
                    label={application.children ?? 'undefined'}
                    startIcon={application.icon}
                    onClick={() => handleOnOptionClick(application)}
                  />
                ))}
              </Box>
            </Menu.Section>
          )}
        </Box>
      }
    >
      <Button>
        <Icon color="currentColor" source={<EllipsisIcon />} />
        {tComm('moreOptions')}
      </Button>
    </Popover>
  );
}

export default ExtraOptionsHeaderDesktop;
